import { Controller } from "@hotwired/stimulus";
import type { createRenderer } from "../maps/renderer";

// Connects to data-controller="saved-map-preview" (app/views/saved_maps/_preview.html.erb): the map as players would see it, drawn by the
// board's renderer from the JSON at `url`, to move around and zoom but not to change.
export default class extends Controller<HTMLElement> {
	static targets = ["box", "canvas", "failure", "fullscreen"];
	static values = { url: String, tiles: Object, monoTiles: Object, rock: String, frameless: Array, monoPlaces: Object };
	declare readonly boxTarget: HTMLElement;
	declare readonly canvasTarget: HTMLElement;
	declare readonly failureTarget: HTMLElement;
	declare readonly fullscreenTarget: HTMLButtonElement;
	declare readonly urlValue: string;
	declare readonly tilesValue: Record<string, string>;
	declare readonly monoTilesValue: Record<string, string>;
	declare readonly rockValue: string;
	declare readonly framelessValue: string[];
	declare readonly monoPlacesValue: Record<string, string>;
	renderer?: ReturnType<typeof createRenderer>;
	abort?: AbortController;

	connect() {
		// Turbo's cache may hand back the page as a failed attempt left it.
		this.canvasTarget.hidden = false;
		this.failureTarget.hidden = true;
		this.fullscreenTarget.hidden = !document.fullscreenEnabled;
		document.addEventListener("fullscreenchange", this.showFullscreen);
		this.draw().catch(() => {
			if (this.abort?.signal.aborted) return;
			this.canvasTarget.hidden = true;
			this.failureTarget.hidden = false;
		});
	}

	disconnect() {
		document.removeEventListener("fullscreenchange", this.showFullscreen);
		this.abort?.abort();
		this.renderer?.destroy();
		this.renderer = undefined;
	}

	async draw() {
		this.abort = new AbortController();
		const { signal } = this.abort;
		const [response, { createSceneState }, { createRenderer }] = await Promise.all([
			fetch(this.urlValue, { headers: { Accept: "application/json" }, signal }),
			import("../live/state"),
			import("../maps/renderer"),
		]);
		if (!response.ok) throw new Error(`The preview answered ${response.status}`);
		const snapshot = await response.json();
		if (signal.aborted) return;
		this.canvasTarget.replaceChildren();
		this.renderer = createRenderer({
			container: this.canvasTarget,
			state: createSceneState(snapshot),
			viewer: { manage: false, accountId: null, table: false },
			measureLabel: () => "",
			tiles: this.tilesValue,
			monoTiles: this.monoTilesValue,
			rock: this.rockValue,
			frameless: this.framelessValue,
			monoPlaces: this.monoPlacesValue,
			// The page scrolls past the map; only Ctrl or Cmd (a trackpad pinch too) zooms it, and full screen has no page to scroll.
			wheelNeedsModifier: () => document.fullscreenElement !== this.boxTarget,
		});
		this.report();
	}

	zoomIn() {
		this.renderer?.zoomIn();
		this.report();
	}

	zoomOut() {
		this.renderer?.zoomOut();
		this.report();
	}

	fit() {
		this.renderer?.fit();
		this.report();
	}

	// Arrows move the map a fifth of the box, + and − zoom, 0 fits it.
	key(event: KeyboardEvent) {
		const renderer = this.renderer;
		if (!renderer || event.altKey || event.ctrlKey || event.metaKey) return;
		const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
		const step = steps[event.key];
		if (step) {
			const { x, y, zoom, width, height } = renderer.viewport();
			renderer.centreAt({ x: (width / 2 - x + (step[0] * width) / 5) / zoom, y: (height / 2 - y + (step[1] * height) / 5) / zoom });
			this.report();
		} else if (event.key === "+" || event.key === "=") this.zoomIn();
		else if (event.key === "-") this.zoomOut();
		else if (event.key === "0") this.fit();
		else return;
		event.preventDefault();
	}

	// The viewport on the element, for the browser tests.
	report() {
		const viewport = this.renderer?.viewport();
		if (!viewport) return;
		this.canvasTarget.dataset.viewport = [viewport.x, viewport.y, viewport.zoom].map((value) => value.toFixed(2)).join(" ");
	}

	toggleFullscreen() {
		if (document.fullscreenElement) document.exitFullscreen?.();
		else this.boxTarget.requestFullscreen?.().catch(() => {});
	}

	showFullscreen = () => {
		this.fullscreenTarget.setAttribute("aria-pressed", String(document.fullscreenElement === this.boxTarget));
	};
}
